/**
 * What each shingle is good for: how long it lasts, and how long the work on
 * it is warranted (ADR 0318).
 *
 * Jonathon, 2026-10-27, over a picture of three new construction roofs that
 * all read "20+ Year Life Expectancy" and "5 Year Warranty": "tamko should
 * reflect 5 years, hdz 10, uhdz 15. life expectancy is 20 tamko, 30 hdz,
 * uhdz 40."
 *
 * Read on a new construction roof, which is one template priced with
 * whichever shingle the builder wants, so the template cannot carry a Key
 * Differentiator that is true of all three. A retail roof is one template per
 * shingle and says its own line, which is not touched here.
 */
import { isLaborOnlyPackage } from "./laborOnly.js";
import { shingleLineFor } from "./shingleColors.js";

/** The years a shingle lineup is sold on. */
export interface ShingleTerms {
  /** `ShingleLine.key`: "titan-xt", "hdz", "uhdz". */
  key: string;
  /** "20+ Year Life Expectancy". */
  lifeYears: number;
  /**
   * "5 Year Warranty": our workmanship warranty, in years. Jonathon,
   * 2026-10-28: "if it's a labor only warranty the workmanship is five years.
   * if we're choosing the gaf etc. template with materials then it's 10 years
   * for hdz, 15 years for uhdz, 5 years for titan."
   */
  warrantyYears: number;
  /** The shingle as the agreement names it: "GAF Timberline HDZ". */
  name: string;
}

/** Jonathon's numbers, 2026-10-27 and 2026-10-28. */
export const SHINGLE_TERMS: readonly ShingleTerms[] = [
  { key: "titan-xt", lifeYears: 20, warrantyYears: 5, name: "TAMKO Titan XT" },
  { key: "hdz", lifeYears: 30, warrantyYears: 10, name: "GAF Timberline HDZ" },
  {
    key: "uhdz",
    lifeYears: 40,
    warrantyYears: 15,
    name: "GAF Timberline UHDZ",
  },
];

/**
 * The terms of the shingle these names describe, or null when none of them
 * names a shingle with terms — a metal roof, a repair, Bare Bones.
 *
 * The same names `shingleLineFor` reads, in the same order: the roof type,
 * then the package's lines, where a shingle names itself.
 */
export function shingleTermsFor(
  names: readonly (string | null | undefined)[],
): ShingleTerms | null {
  const line = shingleLineFor(names);
  if (!line) return null;
  return SHINGLE_TERMS.find((terms) => terms.key === line.key) ?? null;
}

/**
 * The Key Differentiator a shingle earns: "40+ Year Life Expectancy. 15 Year
 * Warranty". Written the way the templates write theirs, so the chart splits
 * it into the same two ticks (`differentiatorPoints`).
 */
export function shingleDifferentiator(
  names: readonly (string | null | undefined)[],
): string | null {
  const terms = shingleTermsFor(names);
  return terms ? differentiatorOf(terms) : null;
}

const differentiatorOf = (terms: ShingleTerms): string =>
  `${terms.lifeYears}+ Year Life Expectancy. ${terms.warrantyYears} Year Warranty`;

/** What `packageShingleTerms` reads of a package. */
export interface ShingleTermsPackage {
  trade?: string | null | undefined;
  roofType?: string | null | undefined;
  name?: string | null | undefined;
  blocks?:
    | readonly {
        lines: readonly { name: string; body: string | null }[];
      }[]
    | null
    | undefined;
}

/**
 * The terms a package reads, or null when it reads none: a roof that is not
 * new construction, one naming no shingle with terms, or a labor only one.
 *
 * Labor only is left out whichever way it was made. The materials are the
 * builder's or the customer's, so the warranty is on our work alone and the
 * package keeps the template's own words — "Installation Labor Only. 5 Year
 * Workmanship Warranty" (ADR 0318).
 *
 * The names are read as `shingleLineFor` reads them: the roof type, then the
 * package's lines, where a shingle names itself.
 */
export function packageShingleTerms(
  pkg: ShingleTermsPackage,
): ShingleTerms | null {
  if (!followsShingleTerms(pkg.trade) || isLaborOnlyPackage(pkg)) return null;
  return shingleTermsFor([
    pkg.roofType,
    ...(pkg.blocks ?? []).flatMap((block) =>
      block.lines.map((line) => line.name),
    ),
  ]);
}

/**
 * The Key Differentiator a package reads when none is typed, or null when it
 * reads none and a blank one leaves the line off (`packageShingleTerms`).
 */
export function packageShingleDifferentiator(
  pkg: ShingleTermsPackage,
): string | null {
  const terms = packageShingleTerms(pkg);
  return terms ? differentiatorOf(terms) : null;
}

/**
 * Whether a roof with this trade reads its shingle's terms when it has no Key
 * Differentiator of its own.
 *
 * Matched on a word, as the trade's other rules are (`isFullShingleReplacement`),
 * because a trade is free text and a new one is added rather than renamed
 * (rule 8). "Roofing New Construction" is the one template sold with a choice
 * of shingle; every retail template is one shingle and carries its own line.
 */
export function followsShingleTerms(trade: string | null | undefined): boolean {
  return (trade ?? "").trim().toLowerCase().includes("new construction");
}

/**
 * The warranty block and the scope on a new construction roof were written
 * for one shingle and promise "5-Year Workmanship" whichever shingle is on
 * it. Read with the shingle's own years instead, so the words under the
 * package cannot contradict the chart beside it. Only the workmanship years
 * move; a "5 year" anywhere else is left as typed.
 */
export function withShingleWarrantyYears(
  text: string,
  terms: ShingleTerms,
): string {
  return text.replace(
    /\b5(?=[ -]year workmanship)/gi,
    String(terms.warrantyYears),
  );
}
